<template>
    <div class="row">
        <SimpleContextualInfo
            ref="SimpleContextualInfo"
            :totalInfo="infoTotal"
            :filteredInfo="filteredInfo"
            @on-apply-filter="onApply"
        />
        <q-card class="row items-start items-stretch col no-wrap q-ml-sm">
            <QBtnDropdown
                dense
                flat
                autoClose
                noCaps
                color="transparent"
                textColor="primary"
                menuAnchor="bottom left"
                menuSelf="top left"
                :menuOffset="[0, 5]"
                class="text-weight-bold"
            >
                <template #label>
                    <QIcon name="filter_alt" color="primary">
                        <QTooltip>
                            {{ $t('visualization.filters.name') }}
                        </QTooltip>
                    </QIcon>
                </template>
                <QList dense class="text-caption">
                    <QItem clickable vClosePopup @click="handleManageFilters">
                        <QItemSection avatar>
                            <QIcon name="filter_alt" color="primary" />
                        </QItemSection>
                        <QItemSection>{{ $t('visualization.filters.sidebarTooltips.addFilter') }}</QItemSection>
                    </QItem>
                    <QItem clickable vClosePopup @click="handleManageFilterSets">
                        <QItemSection avatar>
                            <QIcon name="list" color="primary" />
                        </QItemSection>
                        <QItemSection>{{ $t('visualization.filters.sidebarTooltips.manageFilterSets') }}</QItemSection>
                    </QItem>
                    <QItem clickable vClosePopup @click="handleOpenCreateFilterSetsModal">
                        <QItemSection avatar>
                            <QIcon name="add_circle_outline" color="primary" />
                        </QItemSection>
                        <QItemSection>{{ $t('visualization.filters.sidebarTooltips.createFilterSet') }}</QItemSection>
                    </QItem>
                    <QItem clickable vClosePopup @click="handleResetFilters">
                        <QItemSection avatar>
                            <QIcon name="restart_alt" color="primary" />
                        </QItemSection>
                        <QItemSection>{{ $t('visualization.filters.sidebarTooltips.resetActiveFilters') }}</QItemSection>
                    </QItem>
                    <!-- Operador de filtros -->
                    <QItem
                        v-ripple
                        clickable
                        @mouseenter="aggregationMenu = true"
                        @focus="aggregationMenu = true"
                    >
                        <QItemSection avatar>
                            <QIcon :name="operator === 'AND' ? 'call_merge' : 'call_split'" color="primary" />
                        </QItemSection>
                        <QItemSection>
                            <div class="row items-center">
                                {{ `${$t('visualization.filters.filterSets.config.aggregationOperators.title')}: ` }}
                                <span class="q-ml-sm text-primary text-weight-bold">{{ operator }}</span>
                            </div>
                        </QItemSection>
                        <q-item-section side>
                            <q-icon name="keyboard_arrow_right" />
                        </q-item-section>
                        <QMenu
                            v-model="aggregationMenu"
                            anchor="top end"
                            self="top start"
                            autoClose
                            :active="aggregationMenu"
                        >
                            <QList dense class="text-caption">
                                <QItem
                                    v-for="op in operators"
                                    :key="op.value"
                                    clickable
                                    :active="operator === op.value"
                                    @click="setOperator(op.value); aggregationMenu = false"
                                >
                                    <QItemSection>
                                        <span :class="operator === op.value ? 'text-primary text-weight-bold' : ''">
                                            {{ op.label }}
                                        </span>
                                    </QItemSection>
                                </QItem>
                            </QList>
                        </QMenu>
                    </QItem>
                    <q-item v-ripple tag="label">
                        <q-item-section avatar>
                            <q-icon name="view_compact_alt" color="primary" />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>{{ $t('visualization.filters.sidebarTooltips.compactMode') }}</q-item-label>
                        </q-item-section>
                        <q-item-section side>
                            <q-toggle v-model="compactMode" dense color="primary" val="battery" />
                        </q-item-section>
                    </q-item>
                </QList>
            </QBtnDropdown>
            <div v-if="visibleFilters.length > 0" class="row items-center justify-center q-gutter-x-xs">
                <div
                    v-for="(filter, index) in visibleFilters"
                    :key="`${filter.uuid}-${index}`"
                    color="primary"
                    :class="['q-px-xs flex justify-center filter-card', { 'filter-card--last': index === visibleFilters.length - 1 }]"
                    style="max-width: 320px;"
                    @mouseenter="compactMode && (expandedFilter = index)"
                    @focus="compactMode && (expandedFilter = index)"
                    @mouseleave="compactMode && (expandedFilter = null)"
                    @blur="compactMode && (expandedFilter = null)"
                >
                    <QTooltip
                        v-if="filter.id !== 'FILTER_SET'"
                        contentClass="text-uppercase"
                        maxWidth="300px"
                    >
                        {{ formatLabelValue(filter).tooltip }}
                    </QTooltip>
                    <div class="row items-center justify-center">
                        <!-- Icono -->
                        <FilterIcon
                            :id="getIconId(filter)"
                            :height="28"
                            :width="28"
                            :class="compactMode && expandedFilter === null ? '' : 'q-mr-sm'"
                        />
                        <!-- Texto -->
                        <div
                            v-if="!compactMode || expandedFilter === index"
                            style="flex: 1; min-width: 0; display: flex; flex-direction: column;"
                        >
                            <span
                                class="text-caption text-weight-medium text-primary"
                                :style="{
                                    color: getColor(filter),
                                    lineHeight: '1.2',
                                    maxWidth: '110px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }"
                            >
                                {{ formatLabelValue(filter).label }}
                            </span>
                            <span
                                class="text-caption text-weight-bold text-secondary"
                                :style="{
                                    color: getColor(filter),
                                    lineHeight: '1.2',
                                    maxWidth: '110px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }"
                            >
                                {{ isDateFilter(filter)
                                    ? formatLabelValue(filter).value
                                    : App.numberLocationFormat(formatLabelValue(filter).value, false, true)
                                }}
                                <template v-if="formatLabelValue(filter).additionalValue && formatLabelValue(filter).additionalValue !== 'null-null'">
                                    <br>
                                    <span
                                        class="text-caption"
                                        :style="{
                                            color: getColor(filter),
                                            fontSize: '14px',
                                            lineHeight: '1.2',
                                            maxWidth: '110px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }"
                                    >
                                        {{ formatLabelValue(filter).additionalValue }}
                                    </span>
                                </template>
                            </span>
                        </div>
                        <!-- Botón cerrar -->
                        <QBtn
                            v-if="!compactMode || expandedFilter === index"
                            dense
                            flat
                            round
                            icon="close"
                            color="primary"
                            size="sm"
                            class="q-ml-xs"
                            @click="handleDeleteClick(filter)"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="row items-center q-mx-md">
                {{ $t('visualization.filters.empty') }}
            </div>
        </q-card>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import groupBy from 'lodash/groupBy'
import { mediaQueryMixin, filtersMixin, filtersStorageMixin } from '@/mixins'
import {
    // eslint-disable-next-line max-len
    DATE_FILTERS, VARIANT_FILTERS,
} from '@/screens/Project/Process/Filters/FilterModal/filtersEntity'
import { formatFilterForDisplay, getFilterIconId } from '@/utils/filters'
import { SimpleContextualInfo } from '../Visualization/components'
import FilterIcon from './FilterModal/components/FilterIcon.vue'

export default {
    name: 'ContextFilterBarComponent',
    components: {
        FilterIcon,
        SimpleContextualInfo,
    },
    mixins: [mediaQueryMixin, filtersStorageMixin, filtersMixin],
    inject: ['App'],
    props: {
        processId: VueTypes.oneOfType([String, Number]),
        infoTotal: VueTypes.object,
        filteredInfo: VueTypes.object,
    },
    emits: ['hasLoopFilter', 'onManageCreateFilterSets', 'hasVariantFilter', 'onDeleteClick',
        'onManageFilter', 'onManageFilterSets', 'onReplaceFilters', 'onCancel', 'onReset', 'onApply', 'onRepeatCalls'],
    data() {
        return {
            sets: [],
            isLoading: false,
            variants: undefined,
            operator: 'AND',
            openOptions: false,
            aggregationMenu: false,
            compactMode: false,
            expandedFilter: null,
        }
    },
    computed: {
        totalFilters() {
            return this.visualizationFilters || []
        },
        visibleFilters() {
            return (this.totalFilters || []).filter((f) => !f.hidden)
        },
        filterByName() {
            return groupBy(this.sets, 'name')
        },
        operators() {
            return [
                {
                    label: this.$t('visualization.filters.filterSets.config.aggregationOperators.AND'),
                    value: 'AND',
                },
                {
                    label: this.$t('visualization.filters.filterSets.config.aggregationOperators.OR'),
                    value: 'OR',
                },
            ]
        },
    },
    watch: {
        generalOperator: {
            handler(newOperator) {
                this.operator = newOperator
            },
            immediate: true,
        },
    },
    mounted() {
        this.$eventBus.on('refreshVariantsNamesAtSideBar', (variantInfo) => {
            const { visualizationFilters } = this
            const variantFilter = (visualizationFilters || []).find((filter) => (VARIANT_FILTERS.includes(filter.kind) || VARIANT_FILTERS.includes(filter.id)))
            if (variantFilter) {
                const ids = variantFilter.values || []
                const indexId = ids.indexOf(variantInfo.variantRenamedId)
                if (indexId !== -1) {
                    variantFilter.meta = variantFilter.meta || {}
                    if (!Array.isArray(variantFilter.meta.variantName)) {
                        variantFilter.meta.variantName = []
                    }
                    variantFilter.meta.variantName[indexId] = variantInfo.newName
                }
            }
        })
    },
    methods: {
        handleDeleteClick(selectedFilter) {
            this.$emit('onDeleteClick', selectedFilter)
        },
        handleManageFilters() {
            this.$emit('onManageFilter')
        },
        handleManageFilterSets() {
            this.$emit('onManageFilterSets')
        },
        handleResetFilters() {
            if (this.totalFilters.length > 0) {
                this.$emit('onReset')
            }
        },
        handleOpenCreateFilterSetsModal() {
            this.$emit('onManageCreateFilterSets')
        },
        formatLabelValue(filter = {}) {
            return formatFilterForDisplay(filter, { t: this.$t, variants: this.variants })
        },
        getColor({ pending, toDelete }) {
            if (toDelete) return 'red'
            if (pending) return 'gray03'
            return ''
        },
        getIconId(filter) {
            return getFilterIconId(filter)
        },
        onApply() {
            this.$emit('onApply')
        },
        updateInfoTotal(infoTotal) {
            this.$refs.SimpleContextualInfo.updateInfoTotal(infoTotal)
        },
        setOperator(operator) {
            this.operator = operator
            this.changeGeneralOperator()
        },
        changeGeneralOperator() {
            this.setGeneralOperator(this.operator)
            this.$emit('onRepeatCalls')
        },
        isDateFilter(filter) {
            if (!filter) return false
            if (filter.id === 'FILTER_SET') return true
            if (DATE_FILTERS.includes(filter.id)) return true
            if (/^TRACE_ATTRIBUTES_DATE_/.test(filter.id || '')) return true
            return false
        },
    },
}
</script>

<style scoped lang="scss">
.Filters {
    margin: 10px 5px;
    background: $white;
    display: flex;
    .FilterLabel {
        padding-left: 10px;
        .WText {
            width: auto;
            max-width: 165px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .DeleteIcon {
        padding-left: 5px;
    }
}

.filter-card {
    height: 100%;
    border-left: 1px solid $gray-04;
}

.filter-card--last {
    border-right: 1px solid $gray-04;
}

.FilterIcon {
    background: $primary;
    border-radius: 3px;
    padding: 2px;
}
.ActionContainer {
    display: grid;
    grid-template-columns: 1.2fr 1.2fr;
    grid-template-rows: auto auto;
    height: 100%;
    align-items: center;
    justify-content: space-between;
}
.Column {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
}
.Action {
    margin: 0px 0px;
    flex: 1;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
}
.DropdownAction {
    grid-column: span 2;
    display: flex;
    justify-content: center;
    margin: 0px 8px;

    :deep(.q-btn-dropdown.q-btn-dropdown--simple .q-btn__content .q-btn-dropdown__arrow) {
        margin-left: 4px;
    }
}

.FiltersTextIcon {
    display: flex;
}
</style>
